import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  FaCheckCircle,
  FaShieldAlt,
  FaArrowRight
} from 'react-icons/fa';

// ─── Import Local Images ──────────────────────────────────
import coreBankingImg from '@assets/corebanking.png';
import erpSystemsImg from '@assets/erpystems.png';
import hospitalManagementImg from '@assets/hospitalmanagement.png';
import schoolManagementImg from '@assets/healthcaresystems.png';
import hrPayrollImg from '@assets/hrpayroll.png';
import mobileBankingImg from '@assets/mobilebanking.png';
import bankIntegrationImg from '@assets/bankintegration.png';
import mobileMoneyImg from '@assets/mobilemoney.png';
import aiTrainingImg from '@assets/aitraining.png';

// ─── Constants ────────────────────────────────────────────────────

const SERVICE_IMAGES = {
  'Core Banking': coreBankingImg,
  'ERP Systems': erpSystemsImg,
  'Hospital Management': hospitalManagementImg,
  'School Management': schoolManagementImg,
  'HR & Payroll': hrPayrollImg,
  'Mobile Banking': mobileBankingImg,
  'Bank Integration': bankIntegrationImg,
  'Mobile Money': mobileMoneyImg,
  'AI & Training': aiTrainingImg,
};

const DEFAULT_IMAGE = coreBankingImg;

// ─── SERVICE CATEGORIES WITH CORRECT SLUGS ───
const SERVICE_CATEGORIES = [
  {
    title: 'For Individuals & Organizations',
    items: [
      { 
        label: 'Core Banking', 
        description: 'For Microfinance & SACCO',
        slug: 'power-financials',  // ← Correct slug from productsData
        features: ['Member Management', 'Loan Processing', 'Savings Accounts'],
        isHighlighted: true
      },
      { 
        label: 'ERP Systems', 
        description: 'Dynamics 365 BC',
        slug: 'dynamics-365',  // ← Correct slug from productsData
        features: ['Financials', 'Supply Chain', 'Operations'],
        isHighlighted: true
      }
    ]
  },
  {
    title: 'For Business',
    items: [
      { 
        label: 'Hospital Management', 
        description: 'Healthcare Systems',
        slug: 'hospital-management',  // ← Correct slug from productsData
        features: ['EMR', 'Patient Records', 'Billing']
      },
      { 
        label: 'School Management', 
        description: 'Education Systems',
        slug: 'school-management',  // ← Correct slug from productsData
        features: ['Student Records', 'Exams', 'Fee Processing']
      },
      { 
        label: 'HR & Payroll', 
        description: 'Human Resources',
        slug: 'hr-payroll',  // ← Correct slug from productsData
        features: ['Payroll', 'Leave', 'Claims']
      },
      { 
        label: 'Mobile Banking', 
        description: 'USSD & Apps',
        slug: 'mobile-banking',  // ← Correct slug from productsData
        features: ['USSD', 'Mobile Apps', 'Agency Banking']
      }
    ]
  },
  {
    title: 'For Enterprise',
    items: [
      { 
        label: 'Bank Integration', 
        description: 'API & Switching',
        slug: 'payment-integration',  // ← Correct slug from productsData
        features: ['API Gateway', 'ISO8583', 'ATM Integration']
      },
      { 
        label: 'Mobile Money', 
        description: 'M-PESA & Payments',
        slug: 'mpesa-paybill',  // ← Correct slug from productsData
        features: ['M-PESA', 'B2C', 'C2B']
      },
      { 
        label: 'AI & Training', 
        description: 'Innovation Solutions',
        slug: 'ai-training',  // ← Correct slug from productsData
        features: ['Machine Learning', 'Automation', 'Analytics']
      }
    ]
  }
];

const TRUST_ITEMS = [
  { icon: FaCheckCircle, label: 'Microsoft Dynamics Partner', color: 'text-blue-500' },
  { icon: FaCheckCircle, label: 'M-PESA Integration', color: 'text-emerald-500' },
  { icon: FaShieldAlt, label: 'ISO Compliant', color: 'text-indigo-500' },
];

// ─── Subcomponents ────────────────────────────────────────────────

const ServiceCard = ({ item, index }) => {
  const imageUrl = SERVICE_IMAGES[item.label] || DEFAULT_IMAGE;
  const isHighlighted = item.isHighlighted || false;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: index * 0.05 }}
      viewport={{ once: true }}
    >
      <Link
        to={`/products/${item.slug}`}  // ← Uses correct slug
        className={`group bg-white border ${
          isHighlighted 
            ? 'border-blue-200 hover:border-blue-400 shadow-sm hover:shadow-md' 
            : 'border-slate-200 hover:border-blue-300'
        } rounded-xl overflow-hidden transition-all hover:-translate-y-1 flex flex-col h-full`}
      >
        {/* Image */}
        <div className="relative w-full overflow-hidden bg-slate-100" style={{ aspectRatio: '16/9' }}>
          <img 
            src={imageUrl} 
            alt={item.label}
            loading="lazy"
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
          {isHighlighted && (
            <span className="absolute top-3 right-3 bg-blue-600 text-white text-[10px] px-2.5 py-0.5 rounded-full font-medium shadow-sm">
              Featured
            </span>
          )}
        </div>
        
        {/* Content */}
        <div className="p-4 flex-1 flex flex-col">
          <div className="flex items-start justify-between gap-2">
            <div className="flex-1 min-w-0">
              <h4 className={`${
                isHighlighted 
                  ? 'text-blue-700 text-base font-bold' 
                  : 'text-slate-800 font-semibold text-sm'
              } group-hover:text-blue-600 transition-colors`}>
                {item.label}
              </h4>
              <p className="text-slate-500 text-xs mt-0.5">
                {item.description}
              </p>
            </div>
            {isHighlighted && (
              <span className="text-[8px] bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full uppercase font-bold tracking-wider whitespace-nowrap">
                Popular
              </span>
            )}
          </div>
          
          {/* Features */}
          <div className="mt-2.5 flex flex-wrap gap-1">
            {item.features.slice(0, 3).map((feature, fi) => (
              <span key={fi} className="text-[9px] bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full">
                {feature}
              </span>
            ))}
          </div>
          
          {/* Learn More */}
          <div className="mt-3 pt-2.5 border-t border-slate-100 flex items-center text-xs font-medium text-blue-600 group-hover:gap-2 transition-all gap-1">
            Learn more <FaArrowRight className="text-[10px] group-hover:translate-x-1 transition-transform" />
          </div>
        </div>
      </Link>
    </motion.div>
  );
};

const CategorySection = ({ category, index }) => {
  const getGridCols = (count) => {
    if (count === 4) return 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-4';
    if (count === 3) return 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3';
    return 'grid-cols-1 sm:grid-cols-2';
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      viewport={{ once: true }}
    >
      {/* Category Header */}
      <div className="flex items-center gap-4 mb-5">
        <span className="text-xs font-semibold uppercase tracking-wider text-blue-600 whitespace-nowrap">
          {category.title}
        </span>
        <div className="flex-1 h-px bg-slate-200" />
      </div>

      {/* Service Cards */}
      <div className={`grid ${getGridCols(category.items.length)} gap-4`}>
        {category.items.map((item, i) => (
          <ServiceCard key={i} item={item} index={i} />
        ))}
      </div>
    </motion.div>
  );
};

const TrustBar = () => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.5, delay: 0.3 }}
    viewport={{ once: true }}
    className="mt-12 pt-6 border-t border-slate-200"
  >
    <div className="flex flex-wrap justify-center gap-6 text-slate-600 text-sm">
      {TRUST_ITEMS.map((item, i) => {
        const Icon = item.icon;
        return (
          <span key={i} className="flex items-center gap-1.5">
            <Icon className={`${item.color} text-sm`} />
            {item.label}
          </span>
        );
      })}
    </div>
  </motion.div>
);

// ─── Main Component ──────────────────────────────────────────────

export default function Services() {
  return (
    <section className="py-16 md:py-20 bg-white">
      <div className="container mx-auto px-4 max-w-7xl">
        
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <span className="inline-block bg-blue-50 text-blue-700 px-4 py-1.5 rounded-full text-sm font-semibold mb-4">
            Our Solutions
          </span>
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900">
            What We <span className="text-blue-600">Offer</span>
          </h2>
          <div className="w-16 h-1 bg-blue-600 mx-auto mt-4 rounded-full" />
          <p className="text-slate-600 mt-4 max-w-2xl mx-auto text-base">
            Comprehensive software solutions built for African organizations
          </p>
        </motion.div>

        {/* Service Categories */}
        <div className="space-y-12">
          {SERVICE_CATEGORIES.map((category, idx) => (
            <CategorySection key={idx} category={category} index={idx} />
          ))}
        </div>

        {/* Trust Bar */}
        <TrustBar />

      </div>
    </section>
  );
}